import React, { useEffect } from 'react';

const ASIGraph = () => {
  // Ensure scroll position is at the top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="container mx-auto sm:px-6 md:px-4 sm:py-8 max-w-7xl" style={{ paddingLeft: '0.1rem', paddingRight: '0.1rem', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-8 py-12 text-white">
          <h1 className="text-4xl font-bold mb-4">ASI Graph</h1>
          <p className="text-xl opacity-90">
            Specialised Reasoning Model for Visual Insights
          </p>
        </div>

        {/* Documentation content */}
        <div className="px-6 md:px-12 lg:px-16 py-8">
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              ASI Graph is our closed-source model devoted to one thing: turning data into instantly comprehensible visuals. From quick-and-clean bar charts for dashboards to multi-layer Sankey diagrams for supply-chain audits, ASI Graph fuses advanced reasoning with a built-in visual grammar, giving your agents the power to think in pictures—automatically.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Why Choose ASI Graph?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Rich Visual Vocabulary</h3>
                <p className="text-gray-700">
                  Supports 35+ chart families—bar, line, area, heat-map, treemap, network, Sankey, sunburst, and many more—so every dataset finds its ideal representation.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Native Chart DSL</h3>
                <p className="text-gray-700">
                  Emits Vega-Lite or ECharts JSON out of the box; plug it straight into your front-end with no post-processing.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Context-Aware Styling</h3>
                <p className="text-gray-700">
                  Picks palettes, axis formats, and responsive layouts that match your brand guide or UI theme.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Reason-Driven Visual Choice</h3>
                <p className="text-gray-700">
                  Explains why it selected a heat-map over a table, giving developers transparent logic for audit or override.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Smart Data Wrangling</h3>
                <p className="text-gray-700">
                  Detects missing values, normalises units, and recommends aggregation levels before plotting.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <h3 className="text-lg font-semibold text-blue-700 mb-3">Scalable Rendering Plans</h3>
                <p className="text-gray-700">
                  Splits large datasets into offline-aggregated tiles so dashboards stay snappy even at millions of rows.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Prime Use-Cases</h2>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-300 rounded-lg">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="text-left p-4 border-b font-semibold">Domain</th>
                      <th className="text-left p-4 border-b font-semibold">How ASI Graph Excels</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-4 border-b font-medium">Business Dashboards</td>
                      <td className="p-4 border-b">Auto-generates daily KPI visuals, updates them on schedule, and keeps layouts consistent.</td>
                    </tr>
                    <tr>
                      <td className="p-4 border-b font-medium">Data Storytelling Bots</td>
                      <td className="p-4 border-b">Conversational agents that answer "Show me the sales trend for Q1" with an annotated chart, not just text.</td>
                    </tr>
                    <tr>
                      <td className="p-4 border-b font-medium">DeFi & Web3 Analytics</td>
                      <td className="p-4 border-b">Visualises on-chain flows, gas-cost distributions, and wallet clusters as Sankey or network graphs.</td>
                    </tr>
                    <tr>
                      <td className="p-4 border-b font-medium">Scientific & IoT Monitoring</td>
                      <td className="p-4 border-b">Converts raw sensor streams into anomaly-highlighted timelines and heat-maps in real time.</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-medium">Compliance Reporting</td>
                      <td className="p-4">Produces regulator-ready tables and waterfall charts with foot-noted calculation traces.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Trust & Safety</h2>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
              <div className="overflow-x-auto">
                <table className="w-full border border-gray-300 rounded-lg">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="text-left p-4 border-b font-semibold">Capability</th>
                      <th className="text-left p-4 border-b font-semibold">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-4 border-b font-medium">Verifiable Data Lineage</td>
                      <td className="p-4 border-b">Every chart spec includes a hashed reference to the source dataset and transformation steps.</td>
                    </tr>
                    <tr>
                      <td className="p-4 border-b font-medium">Bias & Mislead Detection</td>
                      <td className="p-4 border-b">Flags cherry-picked ranges or truncated axes that could distort interpretation.</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-medium">Secure Data Handling</td>
                      <td className="p-4">In-memory processing only; no data is written to disk unless explicitly requested by the host application.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed mt-6">
              Put ASI Graph to work whenever numbers need a narrative—then complement it with ASI 1-Mini for general tasks or ASI 1-Mini-Extended for deep audits, giving your closed-source agent stack end-to-end clarity and insight.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ASIGraph;
